import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import {
  getCaseFolders,
  createDefaultFolder,
  deleteCaseFolder,
} from '../../../../services/api';
import ThemeContext from '../../../../ThemeContext';
import FoldersPanel from './FoldersPanel';
import DocumentsPanel from './DocumentsPanel';
import AddFolderModal from './modals/AddFolderModal';
import RenameFolderModal from './modals/RenameFolderModal';
import MoveModal from './modals/MoveModal';
import CopyModal from './modals/CopyModal';
import ConfirmDeleteModal from './modals/ConfirmDeleteModal';

const CaseFoldersSection = ({ caseId, username, sessions = [] }) => {
  const { theme } = useContext(ThemeContext);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Modal states
  const [showAddFolderModal, setShowAddFolderModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [showMoveFolderModal, setShowMoveFolderModal] = useState(false);
  const [showCopyFolderModal, setShowCopyFolderModal] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);


  
  const user = JSON.parse(localStorage.getItem("user"));
const isClientUser = user?.role === "client_user";

  // Ensure caseId is a string
  const caseIdStr = String(caseId);
  console.log('CaseFoldersSection initialized with caseId:', caseIdStr, 'Type:', typeof caseIdStr);

  // Define fetchFolders outside useEffect to make it reusable
  const fetchFolders = async () => {
    try {
      setIsLoading(true);
      console.log('Fetching folders for caseId:', caseIdStr);
      const response = await getCaseFolders(caseIdStr);
      const foldersData = response.data;
      console.log('Folders fetched:', foldersData);

      if (foldersData.length === 0) {
        console.log('No folders found, creating default folder');
        await createDefaultFolder(caseIdStr);
        const newResponse = await getCaseFolders(caseIdStr);
        setFolders(newResponse.data);
        console.log('New folders after default creation:', newResponse.data);
        if (newResponse.data.length > 0) {
          setSelectedFolder(newResponse.data[0]);
          console.log('Set default folder as selected:', newResponse.data[0]);
        }
      } else {
        setFolders(foldersData);
        if (!selectedFolder && foldersData.length > 0) {
          setSelectedFolder(foldersData[0]);
          console.log('Set first folder as selected:', foldersData[0]);
        }
      }

      // Restore selectedFolders from localStorage
      const storedSelectedFolders = JSON.parse(localStorage.getItem(`selectedFolders_${caseIdStr}`) || '[]').map(Number);
      console.log('Restoring selected folders from localStorage:', storedSelectedFolders);
      const validIds = storedSelectedFolders.filter(id => 
        foldersData.some(f => f.id === Number(id) && !f.is_default)
      );
      console.log('Valid selected folder IDs:', validIds);
      setSelectedFolders(validIds);
      if (validIds.length !== storedSelectedFolders.length) {
        console.log('Cleaning up invalid folder IDs in localStorage');
        localStorage.setItem(`selectedFolders_${caseIdStr}`, JSON.stringify(validIds));
      }
    } catch (error) {
      console.error('Failed to fetch folders:', error);
      toast.error(`Failed to fetch folders: ${error.response?.data?.message || error.message}`, {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch folders and initialize selectedFolders on mount
  useEffect(() => {
    if (caseIdStr) {
      fetchFolders();
    }
  }, [caseIdStr]);

  // Save selectedFolders to localStorage whenever it changes
  useEffect(() => {
    if (caseIdStr) {
      console.log('Saving selected folders to localStorage:', selectedFolders, 'for key:', `selectedFolders_${caseIdStr}`);
      localStorage.setItem(`selectedFolders_${caseIdStr}`, JSON.stringify(selectedFolders));
    }
  }, [selectedFolders, caseIdStr]);

  const handleFolderSelect = (folder) => {
    console.log('Folder selected:', folder);
    setSelectedFolder(folder);
    setSelectedFolders([]);
  };

  const handleFolderCheckbox = (folderId) => {
    console.log(`Handling checkbox for folder ID: ${folderId}`);
    if (folderId === 'clear') {
      console.log('Clearing selected folders');
      setSelectedFolders([]);
      return;
    }
    
    setSelectedFolders(prev => {
      if (prev.includes(folderId)) {
        const newSelection = prev.filter(id => id !== folderId);
        console.log('New selected folders after removal:', newSelection);
        return newSelection;
      } else {
        const newSelection = [...prev, folderId];
        console.log('New selected folders after addition:', newSelection);
        return newSelection;
      }
    });
  };

  const handleAddFolder = () => {
    console.log('Opening add folder modal');
    setShowAddFolderModal(true);
  };

  const handleRenameFolder = (folder) => {
    console.log('Opening rename modal for folder:', folder);
    setFolderToEdit(folder);
    setShowRenameFolderModal(true);
  };

  const handleMoveFolder = () => {
    if (selectedFolders.length === 0) {
      console.log('No folders selected for move');
      toast.warning('Please select folders to move', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }
    console.log('Opening move folder modal for folders:', selectedFolders);
    setShowMoveFolderModal(true);
  };

  const handleCopyFolder = () => {
    if (selectedFolders.length === 0) {
      console.log('No folders selected for copy');
      toast.warning('Please select folders to copy', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }
    console.log('Opening copy folder modal for folders:', selectedFolders);
    setShowCopyFolderModal(true);
  };

  const handleDeleteFolder = () => {
    if (selectedFolders.length === 0) {
      console.log('No folders selected for delete');
      toast.warning('Please select folders to delete', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }
    console.log('Opening delete folder modal for folders:', selectedFolders);
    setShowDeleteFolderModal(true);
  };

  const confirmDeleteFolders = async () => {
    try {
      setIsLoading(true);
      console.log('Deleting folders:', selectedFolders);
      for (const folderId of selectedFolders) {
        await deleteCaseFolder(folderId);
      }
      toast.success('Folders deleted successfully', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
        },
      });
      setSelectedFolders([]);
      localStorage.setItem(`selectedFolders_${caseIdStr}`, JSON.stringify([]));
      setShowDeleteFolderModal(false);
      await fetchFolders();
    } catch (error) {
      console.error('Failed to delete folders:', error);
      toast.error(`Failed to delete folders: ${error.response?.data?.message || error.message}`, {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
  style={{
    marginTop: '1rem',
    marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
    border: `1px solid ${theme.colors.borderColor}`,
    borderRadius: '10px',
    backgroundColor: theme.colors.bgPrimary,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'hidden', // keeps inner content inside rounded corners
  }}
>
      <div
        style={{
          display: 'flex',
          gap: 'clamp(0.5rem, 2vw, 1rem)',
          padding: 'clamp(0.5rem, 2vw, 1rem)',
        }}
      >
        <FoldersPanel
          folders={folders}
          selectedFolder={selectedFolder}
          selectedFolders={selectedFolders}
          onFolderSelect={handleFolderSelect}
          onFolderCheckbox={handleFolderCheckbox}
          onAddFolder={handleAddFolder}
          onRenameFolder={handleRenameFolder}
          onMoveFolder={handleMoveFolder}
          onCopyFolder={handleCopyFolder}
          onDeleteFolder={handleDeleteFolder}
          isLoading={isLoading}
          caseId={caseIdStr}
        />
        <DocumentsPanel
  selectedFolder={selectedFolder}
  caseId={caseIdStr}
  username={username}
  onRefresh={fetchFolders}
/>
      </div>
      <AddFolderModal
  show={showAddFolderModal}
  onHide={() => setShowAddFolderModal(false)}
  caseId={caseIdStr}
  sessions={sessions}
  onSuccess={fetchFolders}
/>
      <RenameFolderModal
        show={showRenameFolderModal}
        onHide={() => {
          setShowRenameFolderModal(false);
          setFolderToEdit(null);
        }}
        folder={folderToEdit}
        onSuccess={fetchFolders}
      />
      <MoveModal
        show={showMoveFolderModal}
        onHide={() => setShowMoveFolderModal(false)}
        items={selectedFolders}
        itemType="folder"
        currentCaseId={caseIdStr}
        onSuccess={() => {
          setSelectedFolders([]);
          localStorage.setItem(`selectedFolders_${caseIdStr}`, JSON.stringify([]));
          fetchFolders();
        }}
      />
      <CopyModal
        show={showCopyFolderModal}
        onHide={() => setShowCopyFolderModal(false)}
        items={selectedFolders}
        itemType="folder"
        currentCaseId={caseIdStr}
        onSuccess={() => {
          setSelectedFolders([]);
          localStorage.setItem(`selectedFolders_${caseIdStr}`, JSON.stringify([]));
          fetchFolders();
        }}
      />
      <ConfirmDeleteModal
        show={showDeleteFolderModal}
        onHide={() => setShowDeleteFolderModal(false)}
        onConfirm={confirmDeleteFolders}
        itemCount={selectedFolders.length}
        itemType="folder"
      />
    </div>
  );
};

export default CaseFoldersSection;